import { useCallback, useEffect, useMemo } from "react";
import { Col, Collapse, Row, Spin } from "antd";
import type { CollapseProps } from "antd";
import {
	SubmitButtonOverlay,
	ActionContainer,
	ActionItemsContainer,
	FormHeaderComponent,
} from "../../components/styled/common";
import BackComponent from "../../components/BackComponent";
import CollapseExpandIcon from "../../components/CollapseExpandIcon";
import GalleryMediaPicker from "../../components/GalleryMediaPicker";
import MainButtonComponent from "../../components/MainButtonComponent";
import SelectComponent from "../../components/SelectComponent";
import SwitchOptionComponent from "../../components/SwitchComponent";
import { useVolunteersGalleryContext } from "../../context/VolunteersGalleryContext/VolunteersGalleryContext";
import {
	VOLUNTEERS_GALLERY_LIST_PATH,
	VolunteersGalleryContextType,
	volunteersGalleryContextTypeLabels,
	VolunteersGalleryFormData,
} from "../../context/VolunteersGalleryContext/constants";

const GENERAL_SECTION_KEY = "general";
const MEDIA_SECTION_KEY = "media";
const GALLERY_EXISTS_NOTE = "Galerisi var";

// The notes go before the name: the option list cuts a long project name at its end, and with it a note after it.
const withNotes = (name: string, notes: string[]) => {
	const shown = notes.filter(Boolean);
	return shown.length > 0 ? `(${shown.join(", ")}) ${name}` : name;
};

const AddVolunteersGallery = () => {
	const {
		addVolunteersGallery,
		clearVolunteersGalleryFormData,
		getVolunteersGalleryList,
		volunteersGalleryForm,
		setVolunteersGalleryForm,
		volunteersGalleryList,
		galleryEvents,
		operationStatus,
		uploadProgress,
	} = useVolunteersGalleryContext();

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isAdding = operationStatus.type === "loading" && operationStatus.operation === "add";

	// The galleries are loaded too: a place that already has a gallery cannot get a second one.
	useEffect(() => {
		clearVolunteersGalleryFormData();
		getVolunteersGalleryList();

		return () => {
			clearVolunteersGalleryFormData();
		};
	}, [clearVolunteersGalleryFormData, getVolunteersGalleryList]);

	const handleChange = useCallback((changes: Partial<VolunteersGalleryFormData>) => {
		setVolunteersGalleryForm(prev => ({ ...prev, ...changes }));
	}, [setVolunteersGalleryForm]);

	const hasMainPageGallery = volunteersGalleryList.some(gallery => gallery.contextType === VolunteersGalleryContextType.MainPage);

	const placeOptions = useMemo(() => [
		{
			label: withNotes(volunteersGalleryContextTypeLabels[VolunteersGalleryContextType.MainPage], [hasMainPageGallery ? GALLERY_EXISTS_NOTE : ""]),
			value: String(VolunteersGalleryContextType.MainPage),
			disabled: hasMainPageGallery,
		},
		{
			label: volunteersGalleryContextTypeLabels[VolunteersGalleryContextType.Event],
			value: String(VolunteersGalleryContextType.Event),
		},
	], [hasMainPageGallery]);

	// Every project can be picked, published or not; one that already has a gallery cannot.
	const eventOptions = useMemo(() => {
		const eventsWithGallery = new Set(
			volunteersGalleryList
				.filter(gallery => gallery.contextType === VolunteersGalleryContextType.Event)
				.map(gallery => (gallery.contextId ?? "").toLowerCase())
		);
		return galleryEvents.map(event => {
			const hasGallery = eventsWithGallery.has(event.eventId.toLowerCase());
			return {
				label: withNotes(event.header, [event.isActive ? "" : "Pasif", hasGallery ? GALLERY_EXISTS_NOTE : ""]),
				value: event.eventId,
				disabled: hasGallery,
			};
		});
	}, [galleryEvents, volunteersGalleryList]);

	const items: CollapseProps["items"] = [
		{
			key: GENERAL_SECTION_KEY,
			label: "Genel Bilgiler",
			children: (
				<>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SwitchOptionComponent
								title="Aktiflik Durumu"
								helperText="Aktif olmayan galeri sitede gözükmez."
								value={volunteersGalleryForm.isActive}
								onChange={checked => handleChange({ isActive: checked })}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 16]}>
						<Col xs={24} sm={24} md={12} lg={8}>
							<SelectComponent
								title="Galeri Yeri"
								isRequired
								placeholder="Galerinin gösterileceği yeri seçin"
								options={placeOptions}
								value={volunteersGalleryForm.contextType === null ? null : String(volunteersGalleryForm.contextType)}
								onChange={value => handleChange({ contextType: Number(value) as VolunteersGalleryContextType, contextId: null })}
							/>
						</Col>
						{volunteersGalleryForm.contextType === VolunteersGalleryContextType.Event && (
							<Col xs={24} sm={24} md={12} lg={16}>
								<SelectComponent
									title="Proje"
									isRequired
									showSearch
									placeholder="Galerinin gösterileceği projeyi seçin"
									options={eventOptions}
									value={volunteersGalleryForm.contextId}
									onChange={value => handleChange({ contextId: value })}
								/>
							</Col>
						)}
					</Row>
				</>
			),
		},
		{
			key: MEDIA_SECTION_KEY,
			label: "Görseller ve Videolar",
			children: (
				<GalleryMediaPicker
					title="Galeri Dosyaları"
					files={volunteersGalleryForm.files}
					onChange={files => handleChange({ files })}
					isDisabled={isAdding}
					uploadProgress={uploadProgress}
				/>
			),
		},
	];

	return (
		<>
			<BackComponent text="Galeri Listesine Dön" to={VOLUNTEERS_GALLERY_LIST_PATH} />
			<ActionContainer>
				<Spin spinning={isFetching}>
					<ActionItemsContainer>
						<FormHeaderComponent>Galeri Ekleme</FormHeaderComponent>
						<Collapse
							items={items}
							expandIconPosition="end"
							bordered={false}
							ghost
							expandIcon={CollapseExpandIcon}
							defaultActiveKey={[GENERAL_SECTION_KEY, MEDIA_SECTION_KEY]}
						/>
						<SubmitButtonOverlay>
							<MainButtonComponent onClick={addVolunteersGallery} loading={isAdding}>
								Galeri Ekle
							</MainButtonComponent>
						</SubmitButtonOverlay>
					</ActionItemsContainer>
				</Spin>
			</ActionContainer>
		</>
	);
};

export default AddVolunteersGallery;
